import React, { useState, useEffect } from "react";
import apiService from '../../../config';

const AcquisitionBoundaryAndArea = () => {
    const [tableRows, setTableRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [currentProposalId, setCurrentProposalId] = useState(null);

    // Project Id
    useEffect(() => {
        const getProjectInfo = () => {
            const currentProjectData = localStorage.getItem('currentProjectData');
            if (currentProjectData) {
                try {
                    const projectData = JSON.parse(currentProjectData);
                    return { id: projectData.project_id || projectData.id };
                } catch (e) { console.error(e); }
            }
            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                try {
                    const project = JSON.parse(selectedProject);
                    return { id: project.project_id || project.id };
                } catch (e) { console.error(e); }
            }
            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    return { id: user.data?.project_id || user.project_id };
                } catch (e) { console.error(e); }
            }
            return { id: null };
        };
        const info = getProjectInfo();
        setCurrentProjectId(info.id);
    }, []);

    // Proposal Id
    useEffect(() => {
        const getProposalInfo = () => {
            const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
            try {
                return {
                    proposalId: currentProposal?.data?.default_proposal ||
                        currentProposal?.data?.proposal_id ||
                        currentProposal?.default_proposal ||
                        currentProposal?.proposal_id || null
                };
            } catch (e) { console.error(e); }
            return { proposalId: null };
        };
        const info = getProposalInfo();
        setCurrentProposalId(info.proposalId);
    }, []);

    // Fetch data
    useEffect(() => {
        if (!currentProjectId || !currentProposalId) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiService.getRailwayLandHolding(currentProjectId, {
                    proposalId: currentProposalId
                });

                const landData = response?.data || [];

                const rows = landData.map((parcel, index) => {
                    const owners = parcel.owners || [];
                    const areaA = owners.reduce((sum, o) => sum + (parseFloat(o.area_a) || 0), 0);
                    const areaB = owners.reduce((sum, o) => sum + (parseFloat(o.area_b) || 0), 0);
                    const areaC = owners.reduce((sum, o) => sum + (parseFloat(o.area_c) || 0), 0);
                    const totalAcquired = owners.reduce((sum, o) => sum + (parseFloat(o.owner_aqr_area) || 0), 0);

                    return {
                        sr: index + 1,
                        villageName: parcel.village_name || "-",
                        taluka: parcel.taluka_name || "-",
                        district: parcel.dist_name || "-",
                        a: areaA ? areaA.toFixed(2) : "-",
                        b: areaB ? areaB.toFixed(2) : "-",
                        c: areaC ? areaC.toFixed(2) : "-",
                        acquiredArea: totalAcquired ? totalAcquired.toFixed(2) : "-",
                        gatNo: parcel.survey_no || "-",
                        east: parcel.east || "-",
                        west: parcel.west || "-",
                        north: parcel.north || "-",
                        south: parcel.south || "-"
                    };
                });

                setTableRows(rows);
            } catch (err) {
                console.error('Error fetching acquisition boundary data:', err);
                setError('माहिती लोड करताना त्रुटी आली.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentProjectId, currentProposalId]);

    return (
        <div className="bg-white text-black p-6">
            {/* Title */}
            <div className="text-center mb-6">
                <h2 className="font-semibold text-sm leading-6">
                    प्रस्तावित ब्रॉडगेज गेज परिवर्तनासाठी संपादनासाठी लागणाऱ्या जमिनीचे क्षेत्र व सीमाबाबतची माहिती
                </h2>
            </div>

            {loading && <p className="text-center text-blue-600 mb-4">लोड होत आहे...</p>}
            {error && <p className="text-center text-red-600 mb-4">{error}</p>}

            {/* Table */}
            <table className="w-full border border-black border-collapse text-xs">
                <thead>
                    <tr>
                        <th rowSpan="2" className="border border-black p-2">अ.क्र.</th>
                        <th rowSpan="2" className="border border-black p-2">मौजेचे नाव</th>
                        <th rowSpan="2" className="border border-black p-2">तालुका</th>
                        <th rowSpan="2" className="border border-black p-2">जिल्हा</th>
                        <th colSpan="3" className="border border-black p-2">
                            भूसंपादनासाठी लागणारे क्षेत्र (हेक्टर-आर-गुंठे)
                        </th>
                        <th rowSpan="2" className="border border-black p-2">एकूण संपादित क्षेत्र</th>
                        <th rowSpan="2" className="border border-black p-2">गट नंबर / सर्व्हे नंबर</th>
                        <th colSpan="4" className="border border-black p-2">सीमाबाबतची माहिती</th>
                    </tr>
                    <tr>
                        <th className="border border-black p-2">अ</th>
                        <th className="border border-black p-2">ब</th>
                        <th className="border border-black p-2">क</th>
                        <th className="border border-black p-2">पूर्व</th>
                        <th className="border border-black p-2">पश्चिम</th>
                        <th className="border border-black p-2">उत्तर</th>
                        <th className="border border-black p-2">दक्षिण</th>
                    </tr>
                </thead>

                <tbody>
                    {tableRows.map((row, index) => (
                        <tr key={index}>
                            <td className="border border-black p-2 text-center">{row.sr}</td>
                            <td className="border border-black p-2">{row.villageName}</td>
                            <td className="border border-black p-2">{row.taluka}</td>
                            <td className="border border-black p-2">{row.district}</td>
                            <td className="border border-black p-2 text-center">{row.a}</td>
                            <td className="border border-black p-2 text-center">{row.b}</td>
                            <td className="border border-black p-2 text-center">{row.c}</td>
                            <td className="border border-black p-2 text-center">{row.acquiredArea}</td>
                            <td className="border border-black p-2 text-center">{row.gatNo}</td>
                            <td className="border border-black p-2">{row.east}</td>
                            <td className="border border-black p-2">{row.west}</td>
                            <td className="border border-black p-2">{row.north}</td>
                            <td className="border border-black p-2">{row.south}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Footer */}
            <div className="mt-8 text-right text-xs">
                <p>
                    उपकार्यकारी अभियंता (बांधकाम विभाग) <br />
                    दक्षिण मध्य रेल्वे, अकोला
                </p>
            </div>
        </div>
    );
};

export default AcquisitionBoundaryAndArea;